import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Loader2, Mail, Star, Check, X, Ticket } from 'lucide-react';
import { toast } from 'sonner';
import { authStorage } from '@/utils/authStorage';
import InkPhDLogo from '@/components/InkPhDLogo';
import SeoHead from '@/components/SeoHead';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Admin: contact-form inbox and review moderation. */
export default function AdminInboxPage() {
  const user = authStorage.getAuthorUser();
  const [messages, setMessages] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, r] = await Promise.all([
        axios.get(`${BASE}/contact/messages`, { params: { user_id: user.id } }),
        axios.get(`${BASE}/reviews`, { params: { user_id: user.id } }),
      ]);
      setMessages(m.data.messages || []);
      setReviews(r.data.reviews || []);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  if (denied) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center">
        <div data-testid="inbox-denied">
          <p className="text-lg font-semibold mb-2">Admins only</p>
          <Button asChild variant="outline"><Link to="/thesis">Back to my projects</Link></Button>
        </div>
      </div>
    );
  }

  const moderate = async (id, approved) => {
    await axios.post(`${BASE}/reviews/${id}/approve`, null,
      { params: { user_id: user.id, approved } });
    toast.success(approved ? 'Review published' : 'Review unpublished');
    load();
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0e1a] text-foreground thesis-brand">
      <SeoHead title="Admin inbox" path="/thesis/admin/inbox" noindex
        description="Admin — contact messages and review moderation." />
      <header className="border-b border-border">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/thesis" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            data-testid="inbox-back-link">
            <ArrowLeft className="w-4 h-4" /> Projects
          </Link>
          <div className="flex items-center gap-3">
            <Button asChild size="sm" variant="outline" className="rounded-full" data-testid="inbox-promos-link">
              <Link to="/thesis/admin/promos"><Ticket className="w-3.5 h-3.5 mr-1.5" />Promo codes</Link>
            </Button>
            <InkPhDLogo size="sm" />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-3 sm:px-6 py-10 space-y-6" data-testid="admin-inbox-page">
        <h1 className="text-4xl sm:text-5xl font-serif font-semibold tracking-tight"
          style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
          Admin <span className="text-[#1d4ed8] dark:text-[#7aa9ff]">inbox</span>
        </h1>

        <Card data-testid="inbox-reviews-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-500" /> Reviews ({reviews.length})
            </CardTitle>
            <CardDescription>Reviews are published on the home page only after you approve them.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" />
              : reviews.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="inbox-reviews-empty">No reviews yet.</p>
                : (
                  <ul className="divide-y divide-border" data-testid="inbox-reviews-list">
                    {reviews.map((r) => (
                      <li key={r.id} className="py-3" data-testid={`inbox-review-${r.id}`}>
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="text-sm font-semibold">{r.display_name}</span>
                          <Badge variant="outline" className="text-[10px]">{r.rating}/5</Badge>
                          {r.role && <span className="text-[11px] text-muted-foreground">{r.role}</span>}
                          {r.approved
                            ? <Badge className="text-[10px] bg-emerald-600">published</Badge>
                            : <Badge variant="outline" className="text-[10px]">pending</Badge>}
                          <div className="flex-1" />
                          <Button size="sm" variant="ghost" className="h-7"
                            onClick={() => moderate(r.id, !r.approved)}
                            data-testid={`inbox-review-toggle-${r.id}`}>
                            {r.approved ? <><X className="w-3.5 h-3.5 mr-1" />Unpublish</>
                              : <><Check className="w-3.5 h-3.5 mr-1" />Approve</>}
                          </Button>
                        </div>
                        <p className="text-[13px] leading-relaxed">{r.text}</p>
                      </li>
                    ))}
                  </ul>
                )}
          </CardContent>
        </Card>

        <Card data-testid="inbox-messages-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#1d4ed8]" /> Contact messages ({messages.length})
            </CardTitle>
            <CardDescription>
              Every enquiry is stored here. “not emailed” means RESEND_API_KEY is not configured yet —
              nothing is lost.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" />
              : messages.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="inbox-messages-empty">No messages yet.</p>
                : (
                  <ul className="divide-y divide-border" data-testid="inbox-messages-list">
                    {messages.map((m) => (
                      <li key={m.id} className="py-3" data-testid={`inbox-message-${m.id}`}>
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="text-sm font-semibold">{m.subject}</span>
                          <span className="text-[12px] text-muted-foreground">
                            {m.name} · {m.email}{m.organisation ? ` · ${m.organisation}` : ''}
                          </span>
                          {!m.emailed && <Badge variant="outline" className="text-[10px]">not emailed</Badge>}
                          <div className="flex-1" />
                          <span className="text-[11px] font-mono text-muted-foreground">
                            {String(m.at).slice(0, 16).replace('T', ' ')}
                          </span>
                        </div>
                        <p className="text-[13px] whitespace-pre-wrap leading-relaxed">{m.message}</p>
                      </li>
                    ))}
                  </ul>
                )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
